import { Button, Chip, Icons, Stack, Tooltip, Typography } from '@healthinal/ui';
import { keepPreviousData } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { AssignedToFilter } from '../../api/assignedToFilter.ts';
import { useGetContactTracingCompletionStatus } from '../../api/generated/contact-tracing-controller/contact-tracing-controller.ts';
import {
  CaseManagementDtoContactTracing,
  GetContactTracingCompletionStatus200,
  GetPatientsBasicsType,
} from '../../api/generated/model';
import {
  useGetCaseManagementByPatientId,
  useGetPatientsBasics,
} from '../../api/generated/patient-controller/patient-controller.ts';
import { useGetTracingContactSummary } from '../../api/generated/summary-controller/summary-controller.ts';
import { FETCH_ALL_PAGE_SIZE } from '../../api/pagination.ts';
import { ContentSection } from '../../components/ContentSection.tsx';
import { ErrorBoundary } from '../../components/errors/ErrorBoundary.tsx';
import { MotionDivider } from '../../components/MotionDivider.tsx';
import { StickyContainer } from '../../components/StickyContainer.tsx';
import {
  contactTracingPatientSearchActiveOnly,
  contactTracingPatientSearchTextAtom,
} from '../../state/contactTracingPatientSearchAtoms.ts';
import {
  TracingContactTableFilter,
  tracingContactSearchTextAtom,
  tracingContactSelectedPatientFilterAtom,
  tracingContactTableFilterAtom,
} from '../../state/tracingContactSearchAtoms.ts';
import { formatDateYear } from '../../utils/date.ts';
import { filterTracingContacts } from '../../utils/tracingContactSearch/tracingContactTableFilter.ts';
import { AgeAndAgeCategoryAtCreation } from '../components/AgeAndAgeCategoryAtCreation.tsx';
import { PatientSideListNav } from '../components/PatientSideListNav.tsx';
import { CreateTracingContactDialog } from './components/CreateTracingContactDialog.tsx';
import { ExportTracingContactsDialog } from './components/ExportTracingContactsDialog.tsx';
import { ImportTracingContactsDialog } from './components/ImportTracingContactsDialog.tsx';
import { TracingContactsTable } from './components/tables/TracingContactsTable.tsx';
import { TracingContactTableSearch } from './components/tables/TracingContactTableSearch.tsx';

export const Route = createFileRoute('/contact-tracing/')({
  component: ContactTracingPatients,
});

const PAGE_SIZE = 10;

function ContactTracingPatients() {
  const [activePatientsOnly, setActivePatientsOnly] = useAtom(contactTracingPatientSearchActiveOnly);

  const [patientSearchText, setPatientSearchText] = useAtom(contactTracingPatientSearchTextAtom);
  const [debouncedPatientSearchText, setDebouncedPatientSearchText] = useState(patientSearchText);

  // The patients are searched by the backend, once the user stops typing.
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedPatientSearchText(patientSearchText), 300);
    return () => window.clearTimeout(timeout);
  }, [patientSearchText]);

  const { data: patientsPageData } = useGetPatientsBasics(
    {
      type: GetPatientsBasicsType.CONTACT_TRACING,
      activeOnly: activePatientsOnly,
      assignedTo: AssignedToFilter.ALL,
      size: FETCH_ALL_PAGE_SIZE,
      search: debouncedPatientSearchText.trim() || undefined,
    },
    // Keeps the current list visible while the next search is loading.
    { query: { placeholderData: keepPreviousData } },
  );
  const patients = patientsPageData?.content ?? [];
  const [tracingContactSearchText] = useAtom(tracingContactSearchTextAtom);
  const [tracingContactFilterStatus] = useAtom(tracingContactTableFilterAtom);
  const [selectedPatient, setSelectedPatient] = useAtom(tracingContactSelectedPatientFilterAtom);

  const [page, setPage] = useState(0);

  // The summary API filters by the selected case (contactPatientId), but not by status. With a status
  // filter all contacts are loaded, then filtered and paged here, so the pages only count the contacts
  // that are shown.
  const isFilteredClientSide = tracingContactFilterStatus !== TracingContactTableFilter.ALL;

  const {
    data: contactsPage,
    isLoading,
    isFetching,
  } = useGetTracingContactSummary({
    activeOnly: activePatientsOnly,
    search: tracingContactSearchText || undefined,
    contactPatientId: selectedPatient?.id,
    page: isFilteredClientSide ? 0 : page,
    size: isFilteredClientSide ? FETCH_ALL_PAGE_SIZE : PAGE_SIZE,
  });

  useEffect(() => {
    setPage(0);
  }, [tracingContactSearchText, activePatientsOnly, selectedPatient, tracingContactFilterStatus]);

  const filteredContacts = filterTracingContacts(
    contactsPage?.content ?? [],
    tracingContactFilterStatus,
    selectedPatient,
  );
  const contactsCount = isFilteredClientSide ? filteredContacts.length : (contactsPage?.totalCount ?? 0);
  const totalPages = Math.ceil(contactsCount / PAGE_SIZE);
  const pageContacts = isFilteredClientSide
    ? filteredContacts.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
    : filteredContacts;

  const [isCreateContactOpen, setIsCreateContactOpen] = useState(false);
  const [isImportContactsOpen, setIsImportContactsOpen] = useState(false);
  const [isExportContactsOpen, setIsExportContactsOpen] = useState(false);

  const contentSectionRef = useRef(null);

  const { data: patientCaseManagement } = useGetCaseManagementByPatientId(selectedPatient?.id ?? '', {
    query: { enabled: !!selectedPatient },
  });
  const { data: patientContactTracingCompletedStatus } = useGetContactTracingCompletionStatus(
    selectedPatient?.id ?? '',
    {
      query: { enabled: !!selectedPatient },
    },
  );
  const isCreateNewContactEnabled = selectedPatient
    ? patientCaseManagement?.contactTracing === CaseManagementDtoContactTracing.YES
    : true;

  return (
    <>
      <Stack direction="row" gap={3}>
        <PatientSideListNav
          patients={patients}
          searchText={patientSearchText}
          setSearchText={setPatientSearchText}
          useClientSideSearch={false}
          selectedPatient={selectedPatient}
          setSelectedPatient={setSelectedPatient}
          activeOnly={activePatientsOnly}
          setActiveOnly={setActivePatientsOnly}
          renderPatientLabel={(patient) => (
            <>
              {patient.caseNumber ?? '-'} {patient.firstName} {patient.lastName}
            </>
          )}
        />
        <ContentSection flex={4} ref={contentSectionRef}>
          <Stack gap={2}>
            <StickyContainer gap={2}>
              <Stack gap={2}>
                <Stack direction="row" alignItems="start" justifyContent="space-between">
                  <Stack>
                    {selectedPatient ? (
                      <>
                        <Stack direction={'row'} gap={2} alignItems={'center'}>
                          <Typography
                            level="h3"
                            endDecorator={
                              <Typography fontWeight={400} textColor={'text.tertiary'}>
                                {formatDateYear(selectedPatient.birthDate)}
                              </Typography>
                            }>
                            {selectedPatient.lastName || selectedPatient.firstName
                              ? `${selectedPatient.lastName?.toUpperCase() ?? ''} ${selectedPatient.firstName ?? ''}`.trim()
                              : (selectedPatient.caseNumber ?? '')}
                          </Typography>
                          {patientCaseManagement &&
                            patientContactTracingCompletedStatus &&
                            getContactTracingStatusForPatient(
                              patientCaseManagement.contactTracing,
                              patientContactTracingCompletedStatus,
                            )}
                        </Stack>
                        <Typography level={'body-lg'}>
                          Alter: <AgeAndAgeCategoryAtCreation patient={selectedPatient} />
                        </Typography>
                      </>
                    ) : (
                      <Typography level="h3">Alle Patienten</Typography>
                    )}
                  </Stack>
                  <Stack direction={'row'} gap={1}>
                    <Button endDecorator={<Icons.Download />} onClick={() => setIsExportContactsOpen(true)}>
                      Kontaktliste exportieren
                    </Button>
                    <Button
                      endDecorator={<Icons.UploadFile />}
                      disabled={!isCreateNewContactEnabled}
                      onClick={() => isCreateNewContactEnabled && setIsImportContactsOpen(true)}>
                      Kontaktliste importieren
                    </Button>
                    <Button
                      endDecorator={<Icons.Add />}
                      disabled={!isCreateNewContactEnabled}
                      onClick={() => isCreateNewContactEnabled && setIsCreateContactOpen(true)}>
                      Neuen Kontakt erstellen
                    </Button>
                  </Stack>
                </Stack>
                <TracingContactTableSearch />
              </Stack>
              <MotionDivider contentSectionRef={contentSectionRef} />
            </StickyContainer>
            <ErrorBoundary>
              <TracingContactsTable
                contacts={pageContacts}
                contactsCount={contactsCount}
                isLoading={isLoading || isFetching}
                allContactsCount={contactsPage?.totalCount ?? 0}
                page={page}
                totalPages={totalPages}
                onPrevPage={() => setPage((p) => Math.max(0, p - 1))}
                onNextPage={() => setPage((p) => Math.min(Math.max(0, totalPages - 1), p + 1))}
                showPatientColumn={!selectedPatient}
              />
            </ErrorBoundary>
          </Stack>
        </ContentSection>
      </Stack>
      <ImportTracingContactsDialog
        preselectedPatient={selectedPatient}
        open={isImportContactsOpen}
        onClose={() => setIsImportContactsOpen(false)}
      />
      <ExportTracingContactsDialog
        preselectedPatient={selectedPatient}
        open={isExportContactsOpen}
        onClose={() => setIsExportContactsOpen(false)}
      />
      <CreateTracingContactDialog
        preselectedPatient={selectedPatient}
        open={isCreateContactOpen}
        onClose={() => setIsCreateContactOpen(false)}
      />
    </>
  );
}

function getContactTracingStatusForPatient(
  contactTracing: CaseManagementDtoContactTracing,
  contactTracingCompletedStatus: GetContactTracingCompletionStatus200,
) {
  switch (contactTracing) {
    case CaseManagementDtoContactTracing.NO:
      return (
        <Chip variant={'solid'} color={'success'}>
          Kein Contact Tracing nötig
        </Chip>
      );
    case CaseManagementDtoContactTracing.COMPLETED:
      return (
        <Tooltip title={'Keine einschätzung vorhanden'}>
          <Chip variant={'solid'}>Abgeschlossen</Chip>
        </Tooltip>
      );
    case CaseManagementDtoContactTracing.YES:
      switch (contactTracingCompletedStatus) {
        case GetContactTracingCompletionStatus200.COMPLETED:
          return (
            <Chip variant={'solid'} color={'success'}>
              Abgeschlossen
            </Chip>
          );
        case GetContactTracingCompletionStatus200.UNKNOWN:
          return (
            <Tooltip title={'Contact Tracing Notwendig aber noch nicht gestartet'}>
              <Chip variant={'solid'} color={'warning'}>
                Notwendig
              </Chip>
            </Tooltip>
          );
        case GetContactTracingCompletionStatus200.NOT_COMPLETED:
          return (
            <Chip variant={'solid'} color={'success'}>
              Aktiv
            </Chip>
          );
      }
  }
}
